import {
  CheckCircleOutlined as CheckCircleOutlineIcon,
  ErrorOutlined as ErrorOutlineIcon,
  InfoOutlined as InfoOutlinedIcon,
  type SvgIconComponent,
  WarningAmber as WarningAmberIcon,
} from '@mui/icons-material';
import { Box, CircularProgress, Typography } from '@mui/material';

import { type NpiFeedbackSeverity, useNpiValidation } from '../hooks/useNpiValidation';
import type { NpiExpectedType } from '../store/api';

export interface NpiValidationStatusProps {
  /** Current NPI field value. */
  npi?: string | null;
  /** Whether this field should hold an individual or organization NPI. */
  expectedType: NpiExpectedType;
}

const COLOR_BY_SEVERITY: Record<NpiFeedbackSeverity, string> = {
  success: 'success.main',
  warning: 'warning.main',
  error: 'error.main',
  info: 'info.main',
};

const ICON_BY_SEVERITY: Record<NpiFeedbackSeverity, SvgIconComponent> = {
  success: CheckCircleOutlineIcon,
  warning: WarningAmberIcon,
  error: ErrorOutlineIcon,
  info: InfoOutlinedIcon,
};

/**
 * Drop-in, form-library-agnostic inline feedback for an NPI field. Pass the
 * current field value and what type the field expects; it renders an advisory
 * status line (never blocks the save). Used by every NPI entry point.
 */
export function NpiValidationStatus({ npi, expectedType }: NpiValidationStatusProps) {
  const feedback = useNpiValidation(npi, expectedType);

  if (feedback.isChecking) {
    return (
      <Box
        role="status"
        aria-live="polite"
        sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}
      >
        <CircularProgress size={14} thickness={5} />
        <Typography variant="caption" color="text.secondary">
          Checking NPI…
        </Typography>
      </Box>
    );
  }

  if (!feedback.severity) return null;

  const color = COLOR_BY_SEVERITY[feedback.severity];
  const Icon = ICON_BY_SEVERITY[feedback.severity];

  return (
    <Box
      role="status"
      aria-live="polite"
      sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5, color }}
    >
      <Icon fontSize="small" sx={{ color }} />
      <Typography variant="caption" sx={{ color }}>
        {feedback.message}
      </Typography>
    </Box>
  );
}
